import { FC, useEffect, useState } from 'react'
import { t } from 'ttag'
import debug from 'debug'
import { auth0ClientId, EMAIL_NOT_VERIFIED_INSTRUCTIONS, getRedirectUri } from '../utils/Auth0Utils'
import { AppRoot } from '../../models3/AppRoot'
import { retriableFetch } from '../../models3/API'
import { parseJwt } from './Login'
import { LoadingIcon } from '../utils/Icons'
import './SlttAppAuth0Login.css'
import { getOriginOrBaseUrl } from '../utils/LocationHistory'

const log = debug('sltt:SlttAppAuth0Login')

interface ISlttAppAuth0Login { appRoot: AppRoot }
/** This re-implements Auth0.tsx: Auth0Login using rest api.
 * For some reason the auth0-sdk methods like `handleRedirectCallback`
 * returns an error (probably due to the state of the Auth0Provider?)
 */
const SlttAppAuth0Login: FC<ISlttAppAuth0Login> = ({ appRoot }) => {
    const [error, setError] = useState('')
    const [isLoading, setIsLoading] = useState(false)

    const search = new URL(window.location.href).search
    const callbackCode = search && new URLSearchParams(search).get('code')
    const redirectUri = getRedirectUri()

    useEffect(() => {
        const handleAuthCallback = async () => {
            if (!callbackCode) {
                return
            }
            log('### callbackCode', callbackCode)
            try {
                setIsLoading(true)
                const response = await retriableFetch('https://sltt.auth0.com/oauth/token', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                    },
                    body: new URLSearchParams({
                        grant_type: 'authorization_code',
                        client_id: auth0ClientId,
                        code: callbackCode,
                        redirect_uri: redirectUri,
                    }),
                })
                if (!response.ok) {
                    throw new Error(`Error fetching token: ${response.statusText}`)
                }
                window.history.replaceState({}, document.title, window.location.pathname)
                const data = await response.json()
                const { id_token, access_token } = data
                log('### id_token', id_token)
                log('### access_token', access_token)
                const response2 = await retriableFetch('https://sltt.auth0.com/userinfo', {
                    method: 'GET',
                    headers: {
                        Authorization: `Bearer ${access_token}`,
                    },
                })
                if (!response2.ok) {
                    throw new Error(`Error fetching user info: ${response2.statusText}`)
                }
                const userInfo = await response2.json()
                const { email_verified } = userInfo
                if (!email_verified) {
                    const message = EMAIL_NOT_VERIFIED_INSTRUCTIONS
                    setError(message)
                    return
                }

                const decoded = parseJwt(id_token)
                appRoot.setUser(decoded.email, id_token)
            } catch (error) {
                console.error('Error handling redirect callback:', error)
            }
            finally {
                setIsLoading(false)
            }
        }

        handleAuthCallback()
    }, [callbackCode, appRoot, retriableFetch, redirectUri])

    if (window.opener) {
        return null // <div>{t`This page must be opened from the main application window.`}</div>
    }

    if (error) {
        return <pre>{error}</pre>
    }

    if (isLoading) {
        return (<LoadingIcon className='initialization-message-icon' />)
    }

    const baseUrl = getOriginOrBaseUrl()

    return (
        <div className="login-container">
            {!callbackCode && (
                <button
                    type="button"
                    className="login-button"
                    onClick={() => {
                        window.open(
                            `https://sltt.auth0.com/login?client=${auth0ClientId}&response_type=code&redirect_uri=${getRedirectUri()}&scope=openid profile email&prompt=login&ui_locales=${appRoot.rt?.uiLanguage || 'en'}`,
                            '_blank' // Opens in a new tab
                        )
                    }}
                >
                    <span className="fa fa-sign-in-alt login-button-icon" />
                    <span
                        className="login-button-text"
                        data-id="login-button"
                        data-toggle="tooltip"
                        title={t`Sign into SLTT using your browser, then click Open when the browser tries to open sltt-app`} >
                        {t`Sign in`}
                    </span>
                    <img src={`${baseUrl}/sltt.png`} alt="SLTT" className="login-button-image" />
                </button>
            )}
        </div>
    )
}

export default SlttAppAuth0Login
